import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import TextInput from "../ui/TextInput";
import StudioDateInput from "../ui/StudioDateInput";
import Dropdown from "../common/Dropdown";
import MultiSelectDropdown from "../common/MultiSelectDropdown";
import SimpleFormGroup from "../common/SimpleFormGroup";
import { getCompanyFileCategories } from "../../api/companyFileCategories";
import { CompanyFileCategory } from "../../types/requestsOutputs";
import {
    getCompanyFileTags,
    getCompanyUsageTypeMetadatas,
    getCompanyRightsTermMetadatas,
    getCompanyTerritoryMetadatas,
    getCompanyAllowedMediaMetadatas,
    CompanyFileTag,
    CompanyUsageTypeMetadata,
    CompanyRightsTermMetadata,
    CompanyTerritoryMetadata,
    CompanyAllowedMediaMetadata,
} from "../../api/companyMetadata";
import { LANGUAGES } from "../../utils/languages";
import { useLanguage } from "../../context/useLanguage";
import { useDatabase } from "../database/DatabaseContext";
import styles from "./FileMetadataModal.module.css";

interface FileMetadata {
    title: string;
    description: string;
    author: string;
    date: string;
    type: string;
    language: string;
    companyFileCategoryIds: string[];
    companyRagFolderId: string;
    companyFileTagIds: string[];
    rightsOwner: string;
    creditLine: string;
    companyUsageTypeMetadataIds: string[];
    companyRightsTermMetadataIds: string[];
    companyTerritoryMetadataIds: string[];
    companyAllowedMediaMetadataIds: string[];
}

interface FileWithMetadata {
    file: File;
    metadata: FileMetadata;
}

interface FileMetadataModalProps {
    isOpen: boolean;
    onClose: () => void;
    files: File[];
    onUpload: (filesWithMetadata: FileWithMetadata[]) => void;
    folderId?: string;
}

const FileMetadataModal: React.FC<FileMetadataModalProps> = ({ isOpen, onClose, files, onUpload, folderId }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { folders } = useDatabase();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [categories, setCategories] = useState<CompanyFileCategory[]>([]);
    const [tags, setTags] = useState<CompanyFileTag[]>([]);
    const [usageTypes, setUsageTypes] = useState<CompanyUsageTypeMetadata[]>([]);
    const [rightsTerms, setRightsTerms] = useState<CompanyRightsTermMetadata[]>([]);
    const [territories, setTerritories] = useState<CompanyTerritoryMetadata[]>([]);
    const [allowedMedia, setAllowedMedia] = useState<CompanyAllowedMediaMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [filesWithMetadata, setFilesWithMetadata] = useState<FileWithMetadata[]>([]);

    // Track if metadata has been fetched to avoid re-fetching
    const metadataFetchedRef = useRef(false);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            // Portaled menus / date picker must not close the modal
            if (
                target.closest(".rw-select__menu") ||
                target.closest(".rw-select__menu-portal") ||
                target.closest(".react-select__menu") ||
                target.closest(".react-select__menu-portal") ||
                target.closest(".MuiPickersPopper-root") ||
                target.closest(".rw-studio-date-popper")
            ) {
                return;
            }

            if (modalContentRef.current && !modalContentRef.current.contains(target)) {
                onClose();
            }
        },
        [onClose],
    );

    const fetchAllMetadata = async () => {
        if (metadataFetchedRef.current) return; // Don't fetch if already fetched

        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }
            const [categoriesData, tagsData, usageTypesData, rightsTermsData, territoriesData, allowedMediaData] =
                await Promise.all([
                    getCompanyFileCategories({ token }),
                    getCompanyFileTags({ token }),
                    getCompanyUsageTypeMetadatas({ token }),
                    getCompanyRightsTermMetadatas({ token }),
                    getCompanyTerritoryMetadatas({ token }),
                    getCompanyAllowedMediaMetadatas({ token }),
                ]);
            setCategories(categoriesData);
            setTags(tagsData);
            setUsageTypes(usageTypesData);
            setRightsTerms(rightsTermsData);
            setTerritories(territoriesData);
            setAllowedMedia(allowedMediaData);
            metadataFetchedRef.current = true; // Mark as fetched
        } catch (error) {
            console.error("Error fetching metadata:", error);
            setError(t("errorFetchingMetadata") || "Error fetching metadata");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            fetchAllMetadata();
        } else {
            // Reset the flag when modal closes so it fetches fresh data next time
            metadataFetchedRef.current = false;
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, handleOutsideClick]);

    useEffect(() => {
        if (files && files.length > 0) {
            const initial = files.map((file) => ({
                file,
                metadata: {
                    title: "",
                    description: "",
                    author: "",
                    date: "",
                    type: "",
                    language: "",
                    companyFileCategoryIds: [],
                    companyRagFolderId: folderId || "",
                    companyFileTagIds: [],
                    rightsOwner: "",
                    creditLine: "",
                    companyUsageTypeMetadataIds: [],
                    companyRightsTermMetadataIds: [],
                    companyTerritoryMetadataIds: [],
                    companyAllowedMediaMetadataIds: [],
                },
            }));
            setFilesWithMetadata(initial);
            setCurrentFileIndex(0); // Reset to first file
        } else {
            setFilesWithMetadata([]);
            setCurrentFileIndex(0);
        }
    }, [files, folderId]);

    if (!files || files.length === 0) {
        return null;
    }

    const handleMetadataChange = (field: keyof FileMetadata, value: string | string[]) => {
        setFilesWithMetadata((prev) => {
            const updated = [...prev];
            updated[currentFileIndex].metadata[field] = value as any;
            return updated;
        });
    };

    const handleNext = () => {
        if (currentFileIndex < filesWithMetadata.length - 1) {
            setCurrentFileIndex(currentFileIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentFileIndex > 0) {
            setCurrentFileIndex(currentFileIndex - 1);
        }
    };

    const handleUpload = () => {
        onUpload(filesWithMetadata);
        onClose();
    };

    const currentFile = filesWithMetadata[currentFileIndex];

    if (!isOpen) return null;
    if (!filesWithMetadata.length) return null;
    if (!currentFile) return null;

    const modal = (
        <div className={styles.overlay}>
            <div className={styles.content} ref={modalContentRef}>
                <h1 className={styles.title}>{t("fileMetadata") || "File Metadata"}</h1>

                {error && <div className={styles.error}>{error}</div>}

                <div className={styles.progress}>
                    <span className={styles.counter}>
                        {t("file") || "File"} {currentFileIndex + 1} / {filesWithMetadata.length}
                    </span>
                    <span className={styles.filename}>{currentFile?.file.name}</span>
                </div>

                <div className={styles.body}>
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t("generalMetadata")}</h2>

                        <div className={styles.grid}>
                            <SimpleFormGroup title={t("title") || "Title"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterTitle") || "Enter title"}
                                    value={currentFile?.metadata.title || ""}
                                    onChange={(e) => handleMetadataChange("title", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("author") || "Author"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterAuthor") || "Enter author"}
                                    value={currentFile?.metadata.author || ""}
                                    onChange={(e) => handleMetadataChange("author", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("date") || "Date"} className={styles.formGroup}>
                                <StudioDateInput
                                    value={currentFile?.metadata.date || ""}
                                    onChange={(isoDate) => handleMetadataChange("date", isoDate)}
                                    placeholder={t("selectDate") || "Select date"}
                                />
                            </SimpleFormGroup>

                            {/* Description spans 2 columns */}
                            <SimpleFormGroup
                                title={t("description") || "Description"}
                                className={`${styles.formGroup} ${styles.halfWidth}`}
                            >
                                <TextInput
                                    placeholder={t("enterDescription") || "Enter description"}
                                    value={currentFile?.metadata.description || ""}
                                    onChange={(e) => handleMetadataChange("description", e.target.value)}
                                    size="big-with-border"
                                    numberOfLines={3}
                                />
                            </SimpleFormGroup>

                            {/* Language */}
                            <SimpleFormGroup title={t("language") || "Language"} className={styles.formGroup}>
                                <Dropdown
                                    label=""
                                    value={currentFile?.metadata.language || ""}
                                    options={LANGUAGES.map((lang) => ({
                                        value: lang.code,
                                        label:
                                            language === "FR"
                                                ? `${lang.labelFr} (${lang.nativeLabel})`
                                                : `${lang.label} (${lang.nativeLabel})`,
                                    }))}
                                    onChange={(value) => handleMetadataChange("language", value)}
                                    placeholder={t("selectLanguage") || "Select language"}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            {/* Type, Folder in remaining columns */}
                            <SimpleFormGroup title={t("type") || "Type"} className={styles.formGroup}>
                                <Dropdown
                                    label=""
                                    value={currentFile?.metadata.type || ""}
                                    options={[
                                        { value: "text", label: t("text") },
                                        { value: "image", label: t("image") },
                                        { value: "audio", label: t("audio") },
                                        { value: "video", label: t("video") },
                                    ]}
                                    onChange={(value) => handleMetadataChange("type", value)}
                                    placeholder={t("selectType") || "Select content type"}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={t("folderDB") || "Folder"}
                                className={`${styles.formGroup} ${styles.halfWidth}`}
                            >
                                <Dropdown
                                    label=""
                                    value={currentFile?.metadata.companyRagFolderId || ""}
                                    options={folders.map((folder) => ({
                                        value: folder.id,
                                        label: folder.name,
                                    }))}
                                    onChange={(value) => handleMetadataChange("companyRagFolderId", value)}
                                    placeholder={t("selectFolder") || "Select folder (optional)"}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            {/* Categories and Tags */}
                            <SimpleFormGroup title={`${t("tag")} 1`} className={styles.formGroup}>
                                <MultiSelectDropdown
                                    label=""
                                    value={currentFile?.metadata.companyFileCategoryIds || []}
                                    options={categories.map((category) => ({
                                        value: category.id,
                                        label: category.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyFileCategoryIds", values)}
                                    placeholder={`${t("select")} ${t("tag")} 1`}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={`${t("tag")} 2`}
                                className={`${styles.formGroup} ${styles.halfWidth}`}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={currentFile?.metadata.companyFileTagIds || []}
                                    options={tags.map((tag) => ({
                                        value: tag.id,
                                        label: tag.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyFileTagIds", values)}
                                    placeholder={`${t("select")} ${t("tag")} 2`}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                />
                            </SimpleFormGroup>
                        </div>
                    </div>

                    {/* LEGAL METADATA SECTION */}
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t("legalMetadata")}</h2>

                        <div className={styles.grid}>
                            {/* Rights Owner, Credit Line, Usage Type in three columns */}
                            <SimpleFormGroup title={t("rightsOwner") || "Rights Owner"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterRightsOwner") || "Enter rights owner"}
                                    value={currentFile?.metadata.rightsOwner || ""}
                                    onChange={(e) => handleMetadataChange("rightsOwner", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("creditLine") || "Credit Line"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterCreditLine") || "Enter credit line"}
                                    value={currentFile?.metadata.creditLine || ""}
                                    onChange={(e) => handleMetadataChange("creditLine", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={t("metadataUsageType") || "Usage Type"}
                                className={styles.formGroup}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={currentFile?.metadata.companyUsageTypeMetadataIds || []}
                                    options={usageTypes.map((type) => ({
                                        value: type.id,
                                        label: type.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyUsageTypeMetadataIds", values)}
                                    placeholder={t("selectUsageTypes") || "Select usage types"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            {/* Rights Term, Territory, Allowed Media in three columns */}
                            <SimpleFormGroup
                                title={t("metadataRightsTerm") || "Rights Term"}
                                className={styles.formGroup}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={currentFile?.metadata.companyRightsTermMetadataIds || []}
                                    options={rightsTerms.map((term) => ({
                                        value: term.id,
                                        label: term.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyRightsTermMetadataIds", values)}
                                    placeholder={t("selectRightsTerms") || "Select rights terms"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("metadataTerritory") || "Territory"} className={styles.formGroup}>
                                <MultiSelectDropdown
                                    label=""
                                    value={currentFile?.metadata.companyTerritoryMetadataIds || []}
                                    options={territories.map((territory) => ({
                                        value: territory.id,
                                        label: territory.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyTerritoryMetadataIds", values)}
                                    placeholder={t("selectTerritories") || "Select territories"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={t("metadataAllowedMedia") || "Allowed Media"}
                                className={styles.formGroup}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={currentFile?.metadata.companyAllowedMediaMetadataIds || []}
                                    options={allowedMedia.map((media) => ({
                                        value: media.id,
                                        label: media.name,
                                    }))}
                                    onChange={(values) =>
                                        handleMetadataChange("companyAllowedMediaMetadataIds", values)
                                    }
                                    placeholder={t("selectAllowedMedia") || "Select allowed media"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>
                        </div>
                    </div>
                </div>

                <div className={styles.buttons}>
                    {currentFileIndex > 0 && (
                        <Button
                            variant="outline"
                            shape="pill"
                            label={t("previous") || "Previous"}
                            onClick={handlePrevious}
                        />
                    )}
                    {currentFileIndex < filesWithMetadata.length - 1 ? (
                        <Button variant="primary" label={t("next") || "Next"} onClick={handleNext} />
                    ) : (
                        <Button variant="primary" label={t("uploadFiles") || "Upload Files"} onClick={handleUpload} />
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default FileMetadataModal;
